"use client";

import { useState } from "react";
import {
  Paper,
  Group,
  Text,
  Button,
  Drawer,
  Stack,
  Switch,
  Divider,
} from "@mantine/core";
import { getStoredConsent, saveConsent } from "./consent";
import { useConsent } from "./useConsent";

export interface CookieBannerLabels {
  bannerBody?: string;
  acceptAll?: string;
  rejectAll?: string;
  settings?: string;
  settingsTitle?: string;
  essentialTitle?: string;
  essentialDescription?: string;
  analyticsTitle?: string;
  analyticsDescription?: string;
  save?: string;
}

const DEFAULTS: Required<CookieBannerLabels> = {
  bannerBody:
    "We use analytics cookies (Google Analytics, Microsoft Clarity) to improve your experience. Essential cookies are always active.",
  acceptAll: "Accept all",
  rejectAll: "Reject all",
  settings: "Settings",
  settingsTitle: "Cookie preferences",
  essentialTitle: "Essential",
  essentialDescription:
    "Authentication, security, and session management. Always active.",
  analyticsTitle: "Analytics",
  analyticsDescription:
    "Google Analytics and Microsoft Clarity — help us understand usage and improve the product.",
  save: "Save preferences",
};

export interface CookieBannerProps {
  storageKey: string;
  labels?: CookieBannerLabels;
}

export function CookieBanner({ storageKey, labels = {} }: CookieBannerProps) {
  const t = { ...DEFAULTS, ...labels };
  const consent = useConsent(storageKey);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(false);

  // undefined = pre-hydration (don't flash banner), non-null = consent stored (hide banner)
  if (consent === undefined || consent !== null) return null;

  function acceptAll() {
    saveConsent(storageKey, true);
  }

  function rejectAll() {
    saveConsent(storageKey, false);
  }

  function openSettings() {
    const stored = getStoredConsent(storageKey);
    setAnalyticsChecked(stored?.analytics ?? false);
    setDrawerOpen(true);
  }

  function savePreferences() {
    saveConsent(storageKey, analyticsChecked);
    setDrawerOpen(false);
  }

  function acceptAllFromDrawer() {
    setAnalyticsChecked(true);
    saveConsent(storageKey, true);
    setDrawerOpen(false);
  }

  function rejectAllFromDrawer() {
    setAnalyticsChecked(false);
    saveConsent(storageKey, false);
    setDrawerOpen(false);
  }

  return (
    <>
      <Paper
        p="md"
        // Never part of a printed page (the registry's certificates are printed pages).
        className="print:hidden"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 200,
          borderRadius: 0,
          borderTop: "1px solid var(--mantine-color-default-border)",
        }}
      >
        <Group justify="space-between" wrap="wrap" gap="sm" maw={1200} mx="auto">
          <Text size="sm" style={{ flex: 1, minWidth: 200 }}>
            {t.bannerBody}
          </Text>
          <Group gap="xs" wrap="nowrap">
            <Button size="sm" onClick={acceptAll}>
              {t.acceptAll}
            </Button>
            <Button size="sm" variant="default" onClick={rejectAll}>
              {t.rejectAll}
            </Button>
            <Button size="sm" variant="subtle" onClick={openSettings}>
              {t.settings}
            </Button>
          </Group>
        </Group>
      </Paper>

      <Drawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t.settingsTitle}
        position="right"
        size="sm"
      >
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap={2} style={{ flex: 1 }}>
              <Text size="sm" fw={500}>
                {t.essentialTitle}
              </Text>
              <Text size="xs" c="dimmed">
                {t.essentialDescription}
              </Text>
            </Stack>
            <Switch checked disabled aria-label={t.essentialTitle} />
          </Group>

          <Divider />

          <Group justify="space-between" align="flex-start">
            <Stack gap={2} style={{ flex: 1 }}>
              <Text size="sm" fw={500}>
                {t.analyticsTitle}
              </Text>
              <Text size="xs" c="dimmed">
                {t.analyticsDescription}
              </Text>
            </Stack>
            <Switch
              checked={analyticsChecked}
              onChange={(e) => setAnalyticsChecked(e.currentTarget.checked)}
              aria-label={t.analyticsTitle}
            />
          </Group>

          <Divider mt="sm" />

          <Group justify="space-between">
            <Group gap="xs">
              <Button size="sm" variant="default" onClick={rejectAllFromDrawer}>
                {t.rejectAll}
              </Button>
              <Button size="sm" onClick={acceptAllFromDrawer}>
                {t.acceptAll}
              </Button>
            </Group>
            <Button size="sm" variant="light" onClick={savePreferences}>
              {t.save}
            </Button>
          </Group>
        </Stack>
      </Drawer>
    </>
  );
}
